import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtNum, fmtPct, fmtUSD } from "@/lib/format";
import { ArrowUpRight, Circle, ShieldCheck, Gift } from "lucide-react";
import HeroRotator from "@/components/HeroRotator";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const FUND_INCEPTION_DISPLAY = "Sep 1, 2026";
const FUND_TARGET_AUM = 5_000_000;

function fmtMetric(v, kind) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  if (kind === "pct") return `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`;
  if (kind === "usd") return `${v < 0 ? "-" : ""}$${Math.abs(Number(v)).toLocaleString(undefined, {maximumFractionDigits: 2})}`;
  if (kind === "int") return Math.round(Number(v)).toLocaleString();
  if (kind === "days") { const n = Math.max(0, Math.round(Number(v))); return `${n.toLocaleString()} d`; }
  return Number(v).toFixed(2);
}

// Semantic tone per metric — green = investor-good, red = investor-bad, "" = neutral.
function metricTone(key, v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "";
  const n = Number(v);
  switch (key) {
    case "total_pnl":
    case "expectancy":
    case "gain_pct":
    case "avg_win":
      return n > 0 ? "pos" : n < 0 ? "neg" : "";
    case "avg_loss":
    case "commissions":
    case "max_drawdown_pct":
    case "lost_trades":
      return n !== 0 ? "neg" : "";
    case "won_trades":
      return n > 0 ? "pos" : "";
    case "win_rate_pct":
      return n >= 50 ? "pos" : n > 0 ? "neg" : "";
    case "profit_factor":
      return n > 1 ? "pos" : n > 0 ? "neg" : "";
    case "sharpe":
    case "sortino":
    case "calmar":
      return n >= 1 ? "pos" : n < 0 ? "neg" : "";
    default:
      return "";
  }
}

export default function Home() {
  const [funds, setFunds] = useState([]);
  const [market, setMarket] = useState(null);
  const [fundData, setFundData] = useState({}); // { [fund_id]: { metrics, labels, nav } }

  useEffect(() => {
    api.get("/funds").then((r) => setFunds(r.data)).catch(() => {});
    api.get("/markets/snapshot").then((r) => setMarket(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!funds.length) return;
    const active = funds.filter((f) => (f.status || "active") !== "closed");
    const tick = async () => {
      const map = {};
      await Promise.all(active.map(async (f) => {
        const [m, n] = await Promise.all([
          api.get(`/mt5/metrics?fund_id=${f.id}`).then((x) => x.data).catch(() => null),
          api.get(`/mt5/nav-series?fund_id=${f.id}`).then((x) => x.data).catch(() => null),
        ]);
        map[f.id] = { metrics: m?.metrics, labels: m?.labels, trade_count: m?.trade_count_ingested, nav: n };
      }));
      setFundData(map);
    };
    tick();
    const iv = setInterval(tick, 30000); // auto-refresh every 30s
    return () => clearInterval(iv);
  }, [funds]);

  const hasMt5DataAny = Object.values(fundData).some((d) => (d?.metrics?.trade_count || 0) > 0);

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border">
        <HeroRotator />
        <div className="container-tight relative py-28 md:py-36 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-8">
            <div className="kicker mb-6">Piplight Capital · Quantitative Trading Division</div>
            <h1 className="text-5xl md:text-6xl leading-[1.05] mb-8">
              A family of focused funds.<br />
              <span className="gold">One quantitative discipline.</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl leading-relaxed mb-10">
              Piplight Capital's Quantitative Trading Division runs a growing suite of
              specialised funds — each with its own mandate, risk budget, and independent
              NAV — governed by a single quantitative discipline and a per-client
              high-water mark.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link data-testid="hero-apply" to="/apply" className="btn-gold px-6 py-3 text-sm uppercase tracking-widest inline-flex items-center gap-2">
                Apply <ArrowUpRight size={16} />
              </Link>
              <Link data-testid="hero-portal" to="/login" className="border border-border px-6 py-3 text-sm uppercase tracking-widest hover:border-primary">
                Client Login
              </Link>
            </div>
          </div>
          <div className="hidden md:block col-span-4 self-end">
            <div className="border border-border p-5 bg-card/60 backdrop-blur">
              <div className="kicker mb-4 flex items-center gap-2"><Circle size={8} className="fill-green-500 text-green-500" /> Live Market Snapshot</div>
              {(market?.fx || []).slice(0, 4).map((q) => (
                <div key={q.symbol} className="flex justify-between py-1.5 border-b border-border/60 text-sm">
                  <span>{q.symbol}</span>
                  <span className="font-mono">{fmtNum(q.price, 4)} <span className={q.change_pct >= 0 ? "pos" : "neg"}>{q.change_pct >= 0 ? "+" : ""}{q.change_pct.toFixed(2)}%</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="container-tight py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="kicker mb-3">Our Funds</div>
            <h2 className="text-4xl md:text-5xl">A growing family of focused, quantitative funds.</h2>
          </div>
          <Link to="/risk" className="text-sm gold hover:underline">Risk framework →</Link>
        </div>
        <div className="space-y-8">
          {funds.filter((f) => (f.status || "active") !== "closed").map((f) => {
            const fd = fundData[f.id] || {};
            const metrics = fd.metrics;
            const labels = fd.labels || [];
            const nav = fd.nav;
            const hasTrades = (metrics?.trade_count || 0) > 0;
            return (
              <div key={f.id} data-testid={`fund-card-${f.id}`} className="surface p-6 grid md:grid-cols-12 gap-8">
                {/* LEFT · fund info */}
                <div className="md:col-span-5 flex flex-col">
                  <div className="kicker mb-4">{f.code}</div>
                  <h3 className="text-2xl mb-3">{f.name}</h3>
                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed flex-1">{f.tagline}</p>
                  <div className="grid grid-cols-2 gap-4 border-t border-border pt-4">
                    <div>
                      <div className="kicker text-[10px] mb-1">NAV / Unit</div>
                      <div className="font-mono text-lg">{fmtNum(f.nav_per_unit, 2)}</div>
                    </div>
                    <div>
                      <div className="kicker text-[10px] mb-1">Gains · Since Inception</div>
                      <div className="font-mono text-lg" data-testid={`fxa-gains-${f.id}`}>
                        {hasTrades && metrics?.gain_pct != null
                          ? <span className={metrics.gain_pct >= 0 ? "pos" : "neg"}>{fmtMetric(metrics.gain_pct, "pct")}</span>
                          : <span className="text-muted-foreground">— <span className="text-xs">(awaiting first trades)</span></span>}
                      </div>
                    </div>
                    <div>
                      <div className="kicker text-[10px] mb-1">AUM</div>
                      <div className="font-mono text-sm" data-testid={`fxa-aum-${f.id}`}>{fmtUSD(f.target_aum || 0, 0)}</div>
                    </div>
                    <div>
                      <div className="kicker text-[10px] mb-1">Inception · Status</div>
                      <div className="font-mono text-sm" data-testid={`fxa-inception-${f.id}`}>{new Date(f.inception_date).toLocaleDateString(undefined,{year:"numeric",month:"short",day:"numeric"})} · <span className="gold">{f.status || "active"}</span></div>
                    </div>
                  </div>
                  <div className="mt-4 text-xs text-muted-foreground leading-relaxed border-t border-border pt-4">
                    <div className="kicker text-[10px] mb-2">Description</div>
                    {f.description}
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {f.instruments.map((i) => (
                      <span key={i} className="text-[11px] px-2 py-1 border border-border text-muted-foreground font-mono uppercase tracking-widest">{i}</span>
                    ))}
                  </div>
                </div>
                {/* RIGHT · 15 metrics + NAV chart */}
                <div className="md:col-span-7 flex flex-col" data-testid={`mt5-section-${f.id}`}>
                  <div className="flex items-baseline justify-between mb-3">
                    <div className="kicker">Live Trading · 15 Metrics</div>
                    <div className="text-[10px] font-mono text-muted-foreground">
                      {hasTrades ? <>src: <span className="gold">{metrics?.source || "local"}</span> · {metrics.trade_count} trades</> : "awaiting first trades"}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4" data-testid={`mt5-grid-${f.id}`}>
                    {labels.map(([label, key, kind]) => {
                      const v = metrics?.[key];
                      return (
                        <div key={key} className="stat-tile p-3" data-testid={`home-mt5-${f.id}-${key}`}>
                          <div className="kicker text-[9px] mb-1">{label}</div>
                          <div className={`font-mono text-sm ${metricTone(key, v)}`}>{fmtMetric(v, kind)}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-auto pt-3 border-t border-border">
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="kicker text-[10px]">Rolling NAV / Equity Curve</div>
                      <div className="text-[10px] text-muted-foreground font-mono">
                        {nav?.series?.length ? `${nav.series.length} day${nav.series.length !== 1 ? "s" : ""}` : "no series yet"}
                      </div>
                    </div>
                    <div className="h-28" data-testid={`nav-chart-${f.id}`}>
                      {nav?.series?.length ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={nav.series} margin={{top:6,right:6,bottom:0,left:0}}>
                            <defs>
                              <linearGradient id={`gc-${f.id}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#c9a961" stopOpacity={0.35} />
                                <stop offset="100%" stopColor="#c9a961" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <XAxis dataKey="date" tick={{fontSize: 9, fill:"#666"}} tickLine={false} axisLine={false} minTickGap={30} />
                            <YAxis tick={{fontSize: 9, fill:"#666"}} tickLine={false} axisLine={false}
                              domain={["auto","auto"]} tickFormatter={(v)=>`$${(v/1000).toFixed(0)}k`} width={44} />
                            <Tooltip contentStyle={{background:"#0a0e1a", border:"1px solid #333", fontSize: 11}} labelStyle={{color:"#c9a961"}}
                              formatter={(v, k) => k === "equity" ? [`$${Number(v).toLocaleString(undefined,{maximumFractionDigits:0})}`, "Equity"] : [v, k]} />
                            <Area type="monotone" dataKey="equity" stroke="#c9a961" strokeWidth={1.6} fill={`url(#gc-${f.id})`} isAnimationActive={false} />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="h-full flex items-center justify-center text-[11px] text-muted-foreground font-mono border border-dashed border-border">
                          Chart populates automatically once the first trade closes on this fund's MT5.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-tight py-24 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="kicker mb-3">Fund Architecture</div>
            <h2 className="text-4xl leading-tight">Independent NAV per unit. Independent HWM per client.</h2>
          </div>
          <div className="md:col-span-7 space-y-6 text-muted-foreground text-sm leading-relaxed">
            <p>Each Piplight fund maintains its own pooled trading account, its own NAV/unit series, and its own high-water mark per client. A 50% performance fee is charged only on gains above your fund-specific HWM at crystallization.</p>
            <p>Client accounting is unit-based. When you subscribe, your capital is converted into units of the fund at the prevailing NAV. All fund-level P/L is expressed through the fluctuation of NAV per unit — the platform never attributes an individual trade to a client.</p>
            <p>NAVs are calculated daily and reconciled against broker equity. Redemption windows and lock-ups are configurable per fund and clearly surfaced as your "Next Withdrawal Date Available" in the client portal.</p>
          </div>
        </div>
      </section>

      {/* Principal + Gains 100% Insured */}
      <section className="border-t border-border bg-gradient-to-b from-card/40 to-background" data-testid="insurance-section">
        <div className="container-tight py-20">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 border border-primary text-primary">
                  <ShieldCheck size={22} strokeWidth={1.5} />
                </div>
                <div className="kicker">Capital Protection</div>
              </div>
              <h2 className="text-4xl leading-tight mb-4">
                Principal deposits <span className="gold">and</span> realized gains are insured at
                <span className="gold"> 100%</span>.
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Every dollar of client principal, and every dollar of realized gain sitting inside a client account, is covered end-to-end by our banking and liquidity partners. This is a structural protection — not a marketing claim.
              </p>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
              <div className="surface p-5">
                <div className="kicker text-[10px] mb-2 text-primary">Coverage Ratio</div>
                <div className="font-mono text-3xl gold mb-1">100%</div>
                <div className="text-xs text-muted-foreground leading-relaxed">Of client principal deposited across all Piplight funds.</div>
              </div>
              <div className="surface p-5">
                <div className="kicker text-[10px] mb-2 text-primary">Realized Gains</div>
                <div className="font-mono text-3xl gold mb-1">100%</div>
                <div className="text-xs text-muted-foreground leading-relaxed">Of gains credited to the client account are equally protected.</div>
              </div>
              <div className="surface p-5 sm:col-span-2">
                <div className="kicker text-[10px] mb-2 text-primary">Why We Can Offer This</div>
                <div className="text-sm leading-relaxed text-muted-foreground">
                  Piplight Capital's long-standing relationships with tier-1 liquidity providers and custodial banks make it possible to place client capital inside custodial arrangements that carry full principal-and-gain protection. Coverage terms are disclosed in the Subscription Agreement and reviewed with every client during onboarding.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Referral Rewards */}
      <section className="border-t border-border" data-testid="referral-section">
        <div className="container-tight py-20 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 border border-primary text-primary">
                <Gift size={22} strokeWidth={1.5} />
              </div>
              <div className="kicker">Referral Rewards</div>
            </div>
            <h2 className="text-4xl leading-tight mb-4">
              <span className="gold">15%</span> of your referee's profit — paid after 12 months.
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Introduce a new investor and receive a reward equal to <span className="text-foreground">15% of the profit their account generates</span> during their first twelve months. Paid once at the end of that year. No cap on how many people you can refer.
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="surface p-5">
              <div className="kicker text-[10px] mb-2 text-primary">01 · Reward Rate</div>
              <div className="font-mono text-2xl gold mb-1">15%</div>
              <div className="text-xs text-muted-foreground leading-relaxed">Of profit generated by the referred client.</div>
            </div>
            <div className="surface p-5">
              <div className="kicker text-[10px] mb-2 text-primary">02 · Reward Window</div>
              <div className="font-mono text-2xl gold mb-1">Year 1</div>
              <div className="text-xs text-muted-foreground leading-relaxed">First twelve months from the referee's inception. Paid at the end of that year.</div>
            </div>
            <div className="surface p-5">
              <div className="kicker text-[10px] mb-2 text-primary">03 · Referral Cap</div>
              <div className="font-mono text-2xl gold mb-1">None</div>
              <div className="text-xs text-muted-foreground leading-relaxed">Refer as many qualified investors as you like — every one of them stacks.</div>
            </div>
            <div className="surface p-5 sm:col-span-3">
              <div className="kicker text-[10px] mb-2 text-primary">Example</div>
              <div className="text-sm leading-relaxed text-muted-foreground">
                If you refer an investor who generates <span className="text-foreground font-mono">$100,000</span> in profit during their first year, your reward at year-end is <span className="gold font-mono">$15,000</span>. Refer five investors at that same profit level → <span className="gold font-mono">$75,000</span>. There is no ceiling.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-card/30">
        <div className="container-tight py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="kicker mb-2">Prospective Clients</div>
            <h3 className="text-3xl">Begin your application.</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-lg">Submission does not guarantee acceptance. All applicants are subject to KYC/AML verification and accredited-investor review.</p>
          </div>
          <Link data-testid="cta-apply-bottom" to="/apply" className="btn-gold px-8 py-4 text-sm uppercase tracking-widest">Start Application</Link>
        </div>
      </section>
    </div>
  );
}
